import useStyles from "./styles";
import { useTranslation } from "react-i18next";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { CustomTable } from "../../components/CustomTable/CustomTable";
import { colors } from "../../styles/colors";
import { CustomPreview } from "../../components/CustomPreview/CustomPreview";
import { useState } from "react";
import { ModalAddUserGeneric } from "./ModalAddUserGeneric/ModalAddUserGeneric";
import { ActionsTable } from "./ActionsTable/ActionsTable";
import { CustomStatus } from "../../components/CustomStatus/CustomStatus";
import { ModalSeeUserGeneric } from "./ModalSeeUserGeneric/ModalSeeUserGeneric";
import { ModalSeeConfirmConnectionGeneric } from "./ModalSeeConfirmConnectionGeneric/ModalSeeConfirmConnectionGeneric";
import { ModalSendEmailUserGeneric } from "./ModalSendEmailUserGeneric/ModalSendEmailUserGeneric";
import { ModalDeleteUserGeneric } from "./ModalDeleteUserGeneric/ModalDeleteUserGeneric";
import { ModalChangeStatusGeneric } from "./ModalChangeStatusGeneric/ModalChangeStatusGeneric";
import { CustomLoader } from "../../components/CustomLoader/CustomLoader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getMessageCountUser,
  getMessageHeader,
  getTitleHeaderTable,
} from "../../utils/userGeneric";
import { useGetOneCompanie } from "../../api/companies";
import { Icon } from "../../components/Icon/Icon";
import { CustomButtonTable } from "../../components/CustomButtonTable/CustomButtonTable";
import { ModalInvitFleetManager } from "./ModalInvitFleetManager/ModalInvitFleetManager";
import { PendingInvitations } from "./PendingInvitations/PendingInvitations";
import { useGetCompanyInvitations } from "../../api/invitations";
import { useGetAllUsers } from "../../api/user";
import { CustomCheckboxSelected } from "../../components/CustomCheckboxSelected/CustomCheckboxSelected";
import { MenuItem } from "@mui/material";
import { dataFiltersUsersGeneric } from "./data";
import { useServerPagination } from "../../hooks/useServerPagination";

const columns = [
  // `id` = user.id. It used to render `userID`, which the API populated from
  // company_user.id, so the ID column showed the membership number - and every
  // action built from that row addressed the wrong entity. See
  // ModalDeleteUserGeneric.
  { header: "id", field: "id" },
  { header: "Nom", field: "lastname" },
  { header: "Prénom", field: "firstname" },
  { header: "email", field: "email" },
  { header: "company", field: "name" },
  { header: "company rôle", field: "company_role" },
];

const initUserGenericData = {
  id: -1,
  firstname: "",
  lastname: "",
  email: "",
  is_active: 1,
} as TUserGeneric;

type Props = {};

export const UsersGeneric: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const location = useLocation();

  const navigate = useNavigate();
  const { id = "" } = useParams();
  const companieId = parseInt(id);

  const { companie, isPending: loadingCompanie } = useGetOneCompanie(
    companieId ?? 0
  );

  const pagination = useServerPagination();
  const [filtersSelected, setFiltersSelected] = useState<string | null>(null);

  const {
    users,
    isPending: loadingUser,
    totalCount,
  } = useGetAllUsers(
    pagination.page,
    pagination.perPage,
    pagination.search,
    companie?.name ?? "",
    location.pathname.includes("/admin") ? "FLEET_MANAGER" : null,
    filtersSelected
  );

  const [openModal, setOpenModal] = useState(false);
  const [openModalSee, setOpenModalSee] = useState(false);
  const [openModalConnexion, setOpenModalConnexion] = useState(false);
  const [openModalSendEmail, setOpenModalSendEmail] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalChangeStatus, setOpenModalChangeStatus] = useState(false);
  const [userGeneric, setUserGeneric] =
    useState<TUserGeneric>(initUserGenericData);
  const [openModalInvitUser, setOpenModalInvitUser] = useState(false);

  /*
   * Le compteur d'invitations en attente, a cote du compteur d'utilisateurs.
   *
   * "Nombre d'utilisateurs : 0" etait vrai et parfaitement trompeur : il decrit
   * les rattachements, et une invitation envoyee n'en est pas un. Les deux
   * chiffres cote a cote disent enfin la difference entre "personne n'a ete
   * invite" et "quelqu'un a ete invite et n'est jamais entre".
   *
   * Uniquement sur la fiche d'une societe : ailleurs (liste globale des
   * utilisateurs, admins) il n'y a pas de societe a interroger.
   */
  const isCompanyScreen = location.pathname.includes("/compagnies");
  const { invitations } = useGetCompanyInvitations(
    companieId,
    isCompanyScreen && Number.isFinite(companieId)
  );
  const openInvitationsCount = invitations.filter(
    (i) => i.status !== "accepted"
  ).length;

  const onhandleClose = () => {
    setOpenModal(false);
    setOpenModalSee(false);
    setOpenModalSendEmail(false);
    setOpenModalConnexion(false);
    setOpenModalDelete(false);
    setOpenModalChangeStatus(false);
    setUserGeneric(initUserGenericData);
    setOpenModalInvitUser(false);
  };

  const handleEdit = (user: TUserGeneric) => {
    setOpenModal(true);
    setUserGeneric(user);
  };

  const handleSee = (user: TUserGeneric) => {
    setOpenModalSee(true);
    setUserGeneric(user);
  };

  const handleConnect = (user: TUserGeneric) => {
    setOpenModalConnexion(true);
    setUserGeneric(user);
  };

  const handleSendEmail = (user: TUserGeneric) => {
    setOpenModalSendEmail(true);
    setUserGeneric(user);
  };

  const handleDelete = (user: TUserGeneric) => {
    setOpenModalDelete(true);
    setUserGeneric(user);
  };

  const handleChangeStatus = (user: TUserGeneric) => {
    setOpenModalChangeStatus(true);
    setUserGeneric(user);
  };

  const handleChangeFilters = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFiltersSelected(e.target.value as any);
    // A new filter changes the result set: go back to the first page.
    pagination.resetToFirstPage();
  };

  const actionsColumns = [
    {
      header: "Statut",
      field: "status",
      render: (data: TUserGeneric) => {
        const dataTrue = {
          id: data?.id,
          company_user_id: data?.company_user_id,
          lastname: data?.lastname,
          firstname: data?.firstname,
          email: data?.email,
          is_active: data?.is_active,
          is_super_admin: data?.is_super_admin,
        } as TUserGeneric;
        const isActive = Number(data?.is_active) === 1 ? true : false;
        return (
          <CustomStatus
            isActive={isActive}
            onClick={() => handleChangeStatus(dataTrue)}
          />
        );
      },
    },
    {
      header: "",
      field: "",
      render: (data: TUserGeneric) => {
        // Deliberately does NOT carry `userID` forward. The modals used to
        // prefer it over `id` and it holds a membership key on any API build
        // older than the getAllUsers() fix; dropping it here makes it
        // impossible for a row action to pick up the wrong id space again.
        const dataTrue = {
          id: data?.id,
          company_user_id: data?.company_user_id,
          lastname: data?.lastname,
          firstname: data?.firstname,
          email: data?.email,
          is_active: data?.is_active,
          is_super_admin: data?.is_super_admin,
        } as TUserGeneric;
        return (
          <ActionsTable
            handleEditUserGeneric={() => handleEdit(dataTrue)}
            handleSeeUserGeneric={() => handleSee(dataTrue)}
            handleConnectUserGeneric={() => handleConnect(dataTrue)}
            handleSendEmailUserGeneric={() => handleSendEmail(dataTrue)}
            handleDeleteUserGeneric={() => handleDelete(dataTrue)}
          />
        );
      },
    },
  ];

  const columnsWithActions = [...columns, ...actionsColumns];

  return (
    <>
      <CustomLoader isActive={loadingUser} />
      <ModalInvitFleetManager
        companieId={companieId}
        open={openModalInvitUser}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />
      <ModalAddUserGeneric
        open={openModal}
        userGenericInitial={userGeneric}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />
      <ModalSeeUserGeneric
        open={openModalSee}
        userGenericInitial={userGeneric}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />
      <ModalSeeConfirmConnectionGeneric
        open={openModalConnexion}
        userGenericInitial={userGeneric}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />
      <ModalSendEmailUserGeneric
        open={openModalSendEmail}
        userGenericInitial={userGeneric}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />
      <ModalDeleteUserGeneric
        open={openModalDelete}
        userGenericInitial={userGeneric}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />
      <ModalChangeStatusGeneric
        open={openModalChangeStatus}
        userGenericInitial={userGeneric}
        onCancel={onhandleClose}
        onClose={onhandleClose}
      />
      <Sidebar
        styles={{
          sidebarRightRoot: classes.vehiculeRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div className={classes.containerHeader}>
          {location.pathname.includes("/compagnies") && (
            <div className={classes.divIconArrow}>
              <Icon
                style={{ cursor: "pointer" }}
                name={"arrowLeft"}
                width={"28px"}
                height={"28px"}
                onClick={() => navigate(-1)}
              />
              {!loadingCompanie && (
                <CustomTypography
                  variant="h2"
                  text={companie?.name ?? ""}
                  isUppercase
                />
              )}
            </div>
          )}
          {!location.pathname.includes("/compagnies") && (
            <CustomTypography
              variant="h2"
              text={getMessageHeader(location)}
              isUppercase
            />
          )}
          {location.pathname.includes("/compagnies") && (
            <CustomButtonTable
              variant={"secondary"}
              textVariant={"link"}
              label={t("user.invitUser", "Invit user")}
              width={"fit-content"}
              fontWeight={"bold"}
              textDecoration={"none"}
              iconName={"plus"}
              padding={"0px 15px"}
              textTransform="uppercase"
              onClick={() => setOpenModalInvitUser(true)}
            />
          )}
        </div>
        <div className={classes.divApercu}>
          <CustomTypography
            text={t("common.preview", "Preview")}
            variant={"h5"}
            isUppercase
          />
          <div className={classes.divApercuFilter}>
            <div className={classes.divApercu2}>
              {!loadingUser && (
                <CustomPreview
                  label={getMessageCountUser(totalCount ?? 0, location)}
                  iconName="userCircle"
                  backgroundIcon={colors.primary}
                />
              )}

              {isCompanyScreen && openInvitationsCount > 0 && (
                <CustomPreview
                  label={t(
                    "invitation.pendingCount",
                    "Invitations en attente : {{count}}",
                    { count: openInvitationsCount }
                  )}
                  iconName="help"
                  backgroundIcon={colors.error}
                />
              )}

              {/* <CustomPreview
                label={`${t("common.enabled")} : 109`}
                iconName="check"
                backgroundIcon={colors.success}
              />
              <CustomPreview
                label={`${t("common.disableds")} : 121`}
                iconName="close"
                backgroundIcon={colors.greydark}
              />
              <CustomPreview
                label={"..."}
                iconName="help"
                backgroundIcon={colors.error}
              /> */}
            </div>
            <div className={classes.divFilter}>
              <CustomTypography
                text={`${t("common.filter", "Filter")} :`}
                variant={"link"}
                fontWeight={"bold"}
                color={colors.darkblack}
              />
              <CustomCheckboxSelected
                onChange={handleChangeFilters}
                value={filtersSelected}
                SelectProps={{
                  multiple: false,
                  renderValue: (selecteds: any): string => {
                    return selecteds === "1" ? "Activé" : "Désactivé";
                  },
                }}
              >
                {dataFiltersUsersGeneric.map((p) => (
                  <MenuItem key={p.id} value={p.value}>
                    {p.name}
                  </MenuItem>
                ))}
              </CustomCheckboxSelected>
            </div>
          </div>
        </div>
        <CustomTable
          isFilterFromBackend
          columns={columnsWithActions}
          data={users}
          isSearchable
          tableMaxHeight={"520px"}
          titleHeader={getTitleHeaderTable(location)}
          tablePagnationCount={totalCount}
          {...pagination.tableProps}
        />
        {isCompanyScreen && Number.isFinite(companieId) && (
          <PendingInvitations companyId={companieId} />
        )}
      </Sidebar>
    </>
  );
};
